import type { SeverityLevel } from '@sentry/core';
import type { RuntimeKind } from '@shared/core';
import type { SentryNamespace } from './interfaces/SentryNamespace';
import type { LoggerAdapter } from '@shared/logger';
import type { LogEntry, LogLevel } from '@shared/logger';
import { LOG_LEVELS, LEVEL_PRIORITY, UNHANDLED_ERROR_TYPES } from '@shared/logger';
import { SENTRY_ERROR_CODES } from './constants/SENTRY_ERROR_CODES';
import { DEBUG_CONSTANTS } from '@shared/constants';
import { type AppError, type AsyncResult, err, ok, getEnvironment, getEnvironmentValue, type EnvironmentValue, tryCatchAsync } from '@shared/utils';

const LOG_SEVERITY_MAP: Record<LogLevel, SeverityLevel> = {
  [LOG_LEVELS.ERROR]: 'error',
  [LOG_LEVELS.WARN]: 'warning',
  [LOG_LEVELS.INFO]: 'info',
  [LOG_LEVELS.DEBUG]: 'debug',
  [LOG_LEVELS.NONE]: 'info',
};

/**
 * @group Logger Adapters
 * @summary
 * <blockquote>Abstract Sentry adapter that handles lazy initialisation, promise-based
 * init locking, log-level filtering, breadcrumb recording, and unhandled
 * exception capture — delegating SDK-specific init to subclasses.</blockquote>
 *
 * @remarks
 * Subclasses must implement `performSentryInit`, `getSentryNamespace`,
 * `getDsnEnvironmentKey`, `getRuntimeTag`, and the init-lock accessors
 * (`getInitPromise` / `setInitPromise`). The base class orchestrates
 * everything else.
 *
 * @example
 * ```typescript
 * import { BaseSentryAdapter } from '@shared/logger/sentry/base';
 *
 * class NodeSentryAdapter extends BaseSentryAdapter {
 *   protected async performSentryInit(dsn, env) { ... }
 *   protected getSentryNamespace() { return NodeSentryAdapter.sentryInstance; }
 *   // ...remaining abstract implementations
 * }
 * ```
 */
export abstract class BaseSentryAdapter implements LoggerAdapter {
  abstract readonly supportedRuntimes: ReadonlyArray<RuntimeKind>;

  private isInitialized = false;

  constructor(protected level: LogLevel) { }

  protected abstract getDsnEnvironmentKey(): EnvironmentValue;
  protected abstract getSentryNamespace(): SentryNamespace | null;
  protected abstract getRuntimeTag(): string;

  protected abstract isSentryReady(): boolean;
  protected abstract getInitPromise(): AsyncResult<void, AppError> | null;
  protected abstract setInitPromise(promise: AsyncResult<void, AppError> | null): void;
  protected abstract performSentryInit(dsn: string, environment: string): AsyncResult<void, AppError>;

  protected async getDsnAndEnvironment(): AsyncResult<{ dsn: string; environment: string }, AppError> {
    const dsnKey = this.getDsnEnvironmentKey();
    const dsnResult = getEnvironmentValue(dsnKey);

    if (dsnResult.isErr) {
      return err({
        errorCode: SENTRY_ERROR_CODES.DSN_REQUIRED,
        errorMessage: `Sentry DSN is required for ${this.getRuntimeTag()} environment`
      });
    }

    const dsn = dsnResult.getSuccess();
    const envResult = getEnvironment();

    if (envResult.isErr) {
      return err({
        errorCode: SENTRY_ERROR_CODES.ENVIRONMENT_NOT_FOUND,
        errorMessage: 'Environment not found'
      });
    }

    const environment = envResult.getSuccess();
    return ok({ dsn, environment });
  }

  protected async initializeSentry(): AsyncResult<void, AppError> {
    if (this.isSentryReady()) {
      return ok(undefined);
    }

    const existingPromise = this.getInitPromise();
    if (existingPromise) {
      return existingPromise;
    }

    const promise = this.doInitializeSentry();
    this.setInitPromise(promise);

    const result = await promise;
    this.setInitPromise(null);
    return result;
  }

  private async doInitializeSentry(): AsyncResult<void, AppError> {
    const dsnAndEnvResult = await this.getDsnAndEnvironment();
    if (dsnAndEnvResult.isErr) {
      return err({
        errorCode: SENTRY_ERROR_CODES.INIT_FAILED,
        errorMessage: `Failed to initialize ${this.getRuntimeTag()} Sentry: ${dsnAndEnvResult.getFail().errorMessage}`
      });
    }

    const { dsn, environment } = dsnAndEnvResult.getSuccess();
    const initResult = await this.performSentryInit(dsn, environment);

    if (initResult.isErr) {
      return err({
        errorCode: SENTRY_ERROR_CODES.INIT_FAILED,
        errorMessage: `Failed to initialize ${this.getRuntimeTag()} Sentry: ${initResult.getFail().errorMessage}`
      });
    }

    return ok(undefined);
  }

  protected async ensureInitialized(): AsyncResult<void, AppError> {
    if (this.isInitialized) {
      return ok(undefined);
    }

    const result = await this.initializeSentry();
    if (result.isErr) {
      return result;
    }

    const sentryNamespace = this.getSentryNamespace();
    if (!sentryNamespace) {
      return err({
        errorCode: SENTRY_ERROR_CODES.NOT_INITIALIZED,
        errorMessage: 'Sentry namespace is not available after initialization'
      });
    }

    this.isInitialized = true;
    return ok(undefined);
  }

  protected shouldLog(entry: LogEntry): boolean {
    return LEVEL_PRIORITY[entry.level] >= LEVEL_PRIORITY[this.level];
  }

  execute(entry: LogEntry): AsyncResult<boolean, AppError> {
    return this.log(entry);
  }

  private async log(entry: LogEntry): AsyncResult<boolean, AppError> {
    if (!this.shouldLog(entry)) {
      return ok(false);
    }

    const initResult = await this.ensureInitialized();
    if (initResult.isErr) {
      return ok(false);
    }

    const envResult = getEnvironment();
    if (envResult.isErr) {
      return err(envResult.getFail());
    }

    const sentryNamespace = this.getSentryNamespace();
    if (!sentryNamespace) {
      return ok(false);
    }

    const severity: SeverityLevel = LOG_SEVERITY_MAP[entry.level];
    const extra = this.buildLogExtra(entry, envResult.getSuccess());

    if (entry.level === LOG_LEVELS.ERROR && this.isUnhandledException(entry)) {
      const captureResult = await this.captureUnhandledException(sentryNamespace, entry, severity, extra);
      if (captureResult.isErr) {
        return captureResult;
      }
    }

    await this.recordBreadcrumb(sentryNamespace, entry, severity, extra);
    return ok(true);
  }

  private buildLogExtra(entry: LogEntry, environment: string): Record<string, unknown> {
    const extra: Record<string, unknown> = {
      timestamp: entry.timestamp,
      level: entry.level,
      environment,
      ...(entry.context || {}),
    };

    if (entry.error) {
      extra.error = {
        message: entry.error.message,
        stack: entry.error.stack,
        name: entry.error.name,
      };
    }

    return extra;
  }

  private isUnhandledException(entry: LogEntry): boolean {
    const errorType = entry.context?.errorType;
    return errorType === UNHANDLED_ERROR_TYPES.UNCAUGHT_EXCEPTION ||
      errorType === UNHANDLED_ERROR_TYPES.UNHANDLED_REJECTION ||
      errorType === UNHANDLED_ERROR_TYPES.ERROR_EVENT ||
      errorType === UNHANDLED_ERROR_TYPES.REACT_ERROR_BOUNDARY;
  }

  private async captureUnhandledException(
    sentry: SentryNamespace,
    entry: LogEntry,
    severity: SeverityLevel,
    extra: Record<string, unknown>,
  ): AsyncResult<void, AppError> {
    const captureResult = await tryCatchAsync(async () => {
      const captureContext = {
        level: severity,
        tags: { runtime: this.getRuntimeTag(), logLevel: entry.level },
        extra,
      };

      if (entry.error) {
        sentry.captureException(entry.error, captureContext);
      } else {
        sentry.captureMessage(entry.message, captureContext);
      }

      if (typeof sentry.flush === 'function') {
        await sentry.flush(this.getFlushTimeoutMs());
      }
    });

    if (captureResult.isErr) {
      return err({
        errorCode: SENTRY_ERROR_CODES.CAPTURE_ERROR,
        errorMessage: `Failed to capture error to Sentry: ${captureResult.getFail().errorMessage}`
      });
    }

    return ok(undefined);
  }

  private async recordBreadcrumb(
    sentry: SentryNamespace,
    entry: LogEntry,
    severity: SeverityLevel,
    extra: Record<string, unknown>,
  ): AsyncResult<void, AppError> {
    return tryCatchAsync(async () => {
      sentry.addBreadcrumb({
        category: 'log',
        message: entry.message,
        level: severity,
        data: extra,
        timestamp: Math.floor(new Date(entry.timestamp).getTime() / DEBUG_CONSTANTS.MILLISECONDS_PER_SECOND),
      });
    });
  }

  async close(): AsyncResult<boolean, AppError> {
    if (this.isInitialized) {
      const sentryNamespace = this.getSentryNamespace();
      if (!sentryNamespace) {
        this.isInitialized = false;
        return ok(true);
      }

      const closeTimeoutMs = this.getCloseTimeoutMs();
      const closeResult = await tryCatchAsync(async () => {
        if (closeTimeoutMs > 0) {
          await sentryNamespace.close(closeTimeoutMs);
        } else {
          await sentryNamespace.close();
        }
      });

      if (closeResult.isErr) {
        const error = closeResult.getFail();
        return err({
          errorCode: SENTRY_ERROR_CODES.CLOSE_ERROR,
          errorMessage: `Failed to close Sentry adapter: ${error.errorMessage}`
        });
      }

      this.isInitialized = false;
      return ok(true);
    }

    return ok(true);
  }

  protected getCloseTimeoutMs(): number {
    return 2000;
  }

  protected getFlushTimeoutMs(): number {
    return 2000;
  }
}
